import { useMemo } from 'react'
import useMeasure from 'react-use-measure'
import { Organization } from '../../../types'
import styles from './Multiples.module.scss'
import { computeDotBarLayout, orgOrInitiativeColor } from './utils'
import { CircleProps } from '../models'
import DotBarChart from './DotBarChart'
import DotLegend from './DotLegend'
import { ComponentType, useInView } from '@react-spring/web'
import Circle from './Circle'
import { CIRCLES_ANIMATE_ONCE } from '../constants'
import { Color } from '../../../constants'

interface Multiple {
  name: string
  orgs: Organization[]
  value?: number | string // Optional display value to replace count
}
interface MultipleDotBarsProps {
  maxNumberOfData?: number
  dataByMultiple: Multiple[]
  gridTemplateColumns?: string
  legendItems?: { name: string; fill: string }[]
  CircleInstance?: ComponentType<CircleProps>
  groupTitle?: string
}

export default function MultipleDotBars(props: MultipleDotBarsProps) {
  const {
    groupTitle,
    dataByMultiple,
    gridTemplateColumns = 'repeat(auto-fit, minmax(200px, 1fr))',
    maxNumberOfData,
    CircleInstance = Circle,
  } = props
  const [ref, { width, height }] = useMeasure()
  const [inViewRef, inView] = useInView({
    amount: 0.1,
    once: CIRCLES_ANIMATE_ONCE,
  })

  const dotBarLayout: CircleProps[] = useMemo(() => {
    const maxNumberOfDataInOneMultiple =
      maxNumberOfData ??
      Math.max(...dataByMultiple.map(({ orgs }) => orgs.length))
    if (!maxNumberOfDataInOneMultiple || width <= 0 || height <= 0) {
      return []
    }

    return computeDotBarLayout({
      width,
      height,
      nCircles: maxNumberOfDataInOneMultiple,
    }).map((circle, index) => ({
      ...circle,
      fill: Color.LightBlack,
      id: `circle-${index}`,
    }))
  }, [width, height, dataByMultiple, maxNumberOfData])

  const dotBarLayoutsByMultiple = useMemo(() => {
    return dataByMultiple.map(({ name, orgs, value }) => {
      return {
        name,
        count: value ?? orgs.length,
        circles: dotBarLayout.map((circle, circleIndex) => ({
          ...circle,
          fill:
            circleIndex < orgs.length && inView
              ? orgOrInitiativeColor({
                  initCommTask: orgs?.[circleIndex]?.initCommTask,
                })
              : Color.LightBlack,
        })),
      }
    })
  }, [dataByMultiple, dotBarLayout, inView])

  const circleRadius = useMemo(() => dotBarLayout?.[0]?.r || 0, [dotBarLayout])

  return (
    <>
      <figure ref={inViewRef}>
        {groupTitle && <p className={styles.groupTitle}>{groupTitle}</p>}
        <div
          className={styles.multiples}
          style={{
            gridTemplateColumns,
          }}
        >
          {dotBarLayoutsByMultiple.map((multiple, index) => (
            <div className={styles.multiple} key={multiple.name}>
              <p className={styles.count}>{multiple.count}</p>
              <p className={styles.title}>{multiple.name}</p>
              <div
                ref={index === 0 ? ref : null}
                className={styles.dotBarWrapper}
              >
                <DotBarChart
                  width={width}
                  height={height}
                  circles={multiple.circles}
                  CircleInstance={CircleInstance}
                />
              </div>
            </div>
          ))}
        </div>

        {props.legendItems && (
          <DotLegend
            legendItems={props.legendItems}
            circleRadius={circleRadius}
            CircleInstance={CircleInstance}
          />
        )}
      </figure>
    </>
  )
}
